import React, { createContext, useContext, useState, useEffect } from "react";
import { auth } from "../config/firebaseConfig";
import { getApiUrl, getApiHeaders } from "../config/api";

const SettingsContext = createContext();

export const SettingsProvider = ({ children }) => {
  const [theme, setTheme] = useState("light");
  const [accent, setAccent] = useState("blue");
  const [language, setLanguage] = useState("auto");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Helper: fetch preferences from backend
  const fetchPreferences = async (idToken) => {
    try {
      const res = await fetch(getApiUrl("api/user/preferences"), {
        headers: getApiHeaders({ Authorization: `Bearer ${idToken}` })
      });
      if (res.ok) {
        const data = await res.json();
        setTheme(data.theme || "light");
        setAccent(data.accent || "green");
        setLanguage(data.language || "auto");
      }
    } catch {}
  };

  // Helper: update preferences in backend
  const updatePreferences = async (prefs) => {
    if (!user) return;
    const idToken = await user.getIdToken();
    await fetch(getApiUrl("api/user/preferences"), {
      method: "POST",
      headers: getApiHeaders({
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`
      }),
      body: JSON.stringify(prefs)
    });
  };

  // On mount, check auth and load preferences
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(true);
      if (firebaseUser) {
        const idToken = await firebaseUser.getIdToken();
        await fetchPreferences(idToken);
      } else {
        // Not logged in: load from localStorage
        setTheme(localStorage.getItem("ufind_theme") || "light");
        setAccent(localStorage.getItem("ufind_accent") || "green");
        setLanguage(localStorage.getItem("ufind_language") || "auto");
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // When preferences change, update backend or localStorage
  useEffect(() => {
    if (loading) return;
    if (user) updatePreferences({ theme, accent, language });
    else localStorage.setItem("ufind_theme", theme);
  }, [theme]);
  useEffect(() => {
    if (loading) return;
    if (user) updatePreferences({ theme, accent, language });
    else localStorage.setItem("ufind_accent", accent);
  }, [accent]);
  useEffect(() => {
    if (loading) return;
    if (user) updatePreferences({ theme, accent, language });
    else localStorage.setItem("ufind_language", language);
  }, [language]);

  const value = {
    theme,
    setTheme,
    accent,
    setAccent,
    language,
    setLanguage,
    loading,
    user,
  };
  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext);
